import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Tag, Sparkles, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROMO_CODE = 'SAVE5';
const STORAGE_KEY = 'exit_intent_shown';

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let timeoutShown = false;

    const handleMouseLeave = (e) => {
      if (timeoutShown) return;
      // Detect mouse leaving from the top of the document (typical exit intent on desktop)
      if (e.clientY <= 0) {
        timeoutShown = true;
        sessionStorage.setItem(STORAGE_KEY, '1');
        setOpen(true);
      }
    };

    const handleMouseOut = (e) => {
      if (timeoutShown) return;
      // Fires when the mouse leaves the document area (e.relatedTarget is null/html)
      if (!e.relatedTarget && e.clientY <= 5) {
        timeoutShown = true;
        sessionStorage.setItem(STORAGE_KEY, '1');
        setOpen(true);
      }
    };

    // Mobile fallback: show after 30s if user hasn't booked yet
    const mobileTimer = setTimeout(() => {
      if (window.innerWidth < 768 && !timeoutShown) {
        timeoutShown = true;
        sessionStorage.setItem(STORAGE_KEY, '1');
        setOpen(true);
      }
    }, 30000);

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseout', handleMouseOut);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseout', handleMouseOut);
      clearTimeout(mobileTimer);
    };
  }, []);

  const handleClose = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/exit-intent`, {
        email,
        source: 'exit_intent_popup',
        promo_code: PROMO_CODE,
      });
    } catch (err) {
      // Fail silently - the user still gets the code
      console.warn('Lead capture failed', err);
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  const handleBookNow = () => {
    setOpen(false);
    navigate(`/book?promo=${PROMO_CODE}`);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={handleClose}
      data-testid="exit-intent-overlay"
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 border border-amber-500/30 rounded-3xl p-8 shadow-2xl shadow-amber-500/10 animate-pop-in"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-popup"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          data-testid="exit-intent-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {!submitted ? (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
                Wait! Special Offer
              </span>
            </div>

            <h2 className="text-3xl font-bold text-white mb-2 leading-tight">
              Get an extra <span className="text-amber-400">5% OFF</span>
              <br />
              your transfer
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Stack it with our round-trip & early-booking discounts.
              Use code <span className="font-mono font-bold text-amber-400">{PROMO_CODE}</span> at checkout.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="exit-intent-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full h-12 px-4 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                data-testid="exit-intent-email-input"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="exit-intent-submit"
              >
                <Tag className="w-4 h-4" />
                Claim My 5% Off
              </button>
            </form>
            <p className="text-[11px] text-zinc-500 text-center mt-3">
              No spam. Just your code & occasional Santorini travel tips.
            </p>
          </>
        ) : (
          <div className="text-center" data-testid="exit-intent-success">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">You're in!</h2>
            <p className="text-zinc-400 mb-6">
              Your code <span className="font-mono font-bold text-amber-400">{PROMO_CODE}</span> is active.
              Apply it now and save 5% extra.
            </p>
            <button
              onClick={handleBookNow}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-semibold transition-all flex items-center justify-center gap-2"
              data-testid="exit-intent-book-now"
            >
              Book Now & Save
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
